<template>
  <div class="page-container-detail">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form inline :model="pageData">
        <div class="form-left-items">
          <el-form-item label="">
            <el-radio-group v-model="pageData.rangeType" size="default" @change="rangeTypeChange">
              <el-radio-button label="month">{{ $str.getKey('数据分析/按月查询') }}</el-radio-button>
              <el-radio-button label="day">{{ $str.getKey('数据分析/按日查询') }}</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$str.getKey('数据分析/日期')">
            <el-date-picker v-if="pageData.rangeType == 'month'" v-model="pageData.month" type="month"
              :clearable="false" @change="timeRangeChange" :disabled-date="disabledDate" size="default"
              class="filter-date-picker"></el-date-picker>
            <el-date-picker v-if="pageData.rangeType == 'day'" v-model="pageData.day" type="date" :clearable="false"
              @change="timeRangeChange" :disabled-date="disabledDate" size="default"
              class="filter-date-picker"></el-date-picker>
          </el-form-item>
        </div>
      </el-form>
      <el-form-item>
        <div class="filter-actions">
          <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
            <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
            <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
          </v-btn>
        </div>
      </el-form-item>
    </div>
    <!-- 图表区域 -->
    <div class="main-container">
      <div class="left-container">
        <div class="cards-container" v-if="true">
          <el-scrollbar height="100%">
            <template v-for="cardInfo in pageData.cardList" :key="cardInfo">
              <SelectCard :cardInfo="cardInfo" @selectedChange="handleCardSelectedChange"
                @lineDataChange="handleLineChange"></SelectCard>
            </template>
          </el-scrollbar>
        </div>
        <div class="bottom-container">
          <el-button type="text" @click="pageData.isShowTopo = true">{{ $str.getKey('数据分析/查看拓扑') }}</el-button>
        </div>
      </div>
      <div class="right-container">
        <div class="chart-title">{{ $str.getKey('数据分析/需量跟踪曲线') }}</div>
        <div ref="chartDom" class="chart-container"></div>
        <!-- 加载状态 -->
        <div v-if="chartLoading" class="chart-status">
          <div class="status-content">
            <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 弹窗-查看拓扑 -->
    <viewTopoModal v-if="pageData.isShowTopo" v-model:show="pageData.isShowTopo"
      @SendChange="pageData.isShowTopo = false;" />

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import dayjs from "dayjs";
import { useAppStore } from "@/store/modules/app";
import { cloneDeep } from "@/utils"
import SelectCard from './refs/selectCard/index.vue'; // 左侧选项组卡片
import viewTopoModal from "./refs/topo/viewModal.vue"; // 弹窗-查看拓扑
import { ChartController } from './refs/chartController/index.js';
import { queryDemandMeterList, queryLinesOption } from './refs/api/index.js';

const $str = window.$str;

/** 获取stationId，并监听其变化-更新数据 */
const appStore = useAppStore();
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    initPage(stationId.value);
  }
});


/** 初始化数据 ====== */
const originData = {
  month: dayjs().subtract(1, 'month').toDate(),
  day: dayjs().subtract(1, 'day').toDate(),
  time: dayjs().subtract(1, 'month').toDate(), // 默认选择月份
  rangeType: 'month',
  cardList: [
    {
      groupKey: 'actualDemand',
      name: $str.getKey('数据分析/实际需量'),
      children: [],
    }
  ],
  isShowTopo: false,
  lineCheckedInfo: {
    checked: new Set(),
    unchecked: new Set(),
  },
}
const pageData = reactive(cloneDeep(originData));
const chartDom = ref(null); // 引用echar-dom
let chartController = null; // 引用echarts实例对象
const chartLoading = ref(false); // 图表加载状态

/** ====== [业务：初始化方法] =================================================================== */

onMounted(() => {
  /** 初始化图表控制器 */
  chartController = new ChartController(chartDom.value, pageData.rangeType, pageData.time);
})

/**
 * 基于电站ID初始化页面
 * @param {*} stationId 电站ID
 */
function initPage(stationId) {
  // 清空选中状态
  pageData.lineCheckedInfo.checked.clear();
  pageData.lineCheckedInfo.unchecked.clear();
  chartLoading.value = true;
  onSearch();
}

/** ====== [业务：功能方法] =================================================================== */
/** 查询-更新图表数据 */
function onSearch() {
  // 校验日期
  if (pageData.rangeType === 'month' && !pageData.month) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择月份')
    });
    return false;
  }
  if (pageData.rangeType === 'day' && !pageData.day) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择日期')
    });
    return false;
  }
  updatePageData();
}

/** 重置 */
function onReset() {
  // 清空选中状态
  pageData.lineCheckedInfo.checked.clear();
  pageData.lineCheckedInfo.unchecked.clear();

  chartLoading.value = true;
  const data = cloneDeep(originData);
  Object.keys(data).forEach(key => {
    pageData[key] = data[key];
  })
  initPage();
}

/** 时间范围的类型发生变化 */
function rangeTypeChange(type) {
  // 清空选中状态，避免切换时数据混乱
  pageData.lineCheckedInfo.checked.clear();
  pageData.lineCheckedInfo.unchecked.clear();

  chartLoading.value = true;
  timeRangeChange();
  if (type === 'month') {
    pageData.cardList = [
      {
        groupKey: 'actualDemand',
        name: $str.getKey('数据分析/实际需量'),
        children: [],
      },
    ]
  } else {
    pageData.cardList = [{
      groupKey: 'actualDemand',
      name: $str.getKey('数据分析/实际需量'),
      children: [],
    },
    {
      groupKey: 'energyDemand',
      name: $str.getKey('数据分析/储能需量'),
      children: [],
    },
    {
      groupKey: 'loadDemand',
      name: $str.getKey('数据分析/负荷需量'),
      children: [],
    }]
  }
}

/** 响应选择月份发生变化 */
function timeRangeChange() {
  // 请求数据，更新图表
  pageData.time = pageData.rangeType == 'month' ? pageData.month : pageData.day
  onSearch();
}

/**
 * 事件响应：处理左侧面板勾选项发生变化的事件
 * >>> 数据不变，只处理显隐
 * @param {*} msg 
 */
function handleCardSelectedChange(msg) {
  updateChartRender(); // 更新图表渲染
}

/**
 * 事件响应：图表曲线数据发生变化
 * >>> 新增 | 删除 | 编辑
 * @param {*} msg 
 */
function handleLineChange(msg) {
  onSearch(); // 更新数据-更新图表渲染
}

/** ====== [辅助：功能方法] =================================================================== */
/**
 * 设置日期的禁选范围
 */
function disabledDate(date) {
  if (pageData.rangeType == 'month') {
    return date > dayjs() || date < dayjs().subtract(6, 'month').startOf('month');
  } else if (pageData.rangeType == 'day') {
    return date > dayjs() || date < dayjs().subtract(6, 'month');
  }
  return false;
}

/**
 * 更新页面依赖的数据
 */
function updatePageData() {
  const params = {
    stationId: stationId.value,
    rangeType: pageData.rangeType,
    time: pageData.time,
  }
  chartLoading.value = true;
  const p1 = queryDemandMeterList(params); // 获取图表数据
  const p2 = queryLinesOption(stationId.value); // 获取各分组的需量曲线信息

  Promise.all([p1, p2]).then(res => {
    if (Array.isArray(res) && res.length === 2) {
      const chartData = res[0]; // 新的接口直接返回图表数据
      const lineGroups = res[1];

      // 将接口返回的数据，格式化后，更新到页面的 pageData.cardList 中
      pageData.cardList.forEach(group => {
        console.log('XXX', group, lineGroups)
        const lineList = lineGroups[group.groupKey].map(it => {
          // 在chartData[group.groupKey]中查找同lineUuid的数据
          const chartGroupArr = chartData[group.groupKey] || [];
          const lineChartData = chartGroupArr.find(line => line.lineUuid === it.lineUuid);
          const demandList = lineChartData ? lineChartData.demandList : [];

          const line = {
            name: it.lineName,
            id: it.lineUuid,
            key: it.lineUuid,
            isShow: getLineCheckedState(it.lineUuid),
            data: convertChartDataFormat(demandList),
            addMeterList: it.meterAddList.map(it => it.meterUuid),
            subMeterList: it.meterSubtractList.map(it => it.meterUuid),
          }
          if (!lineChartData) {
            console.warn('未找到曲线' + ` ${it.lineName} (${it.lineUuid}) ` + '的图表数据');
          }
          return line;
        })
        group.children = lineList;
      })

    } else {
      throw Error('接口请求异常！');
    }
  }).catch(err => {
    console.error('接口请求异常！', err);
    // 清空数据&渲染 >>> 视情况而定，是否保留渲染历史数据
    pageData.cardList.forEach(group => {
      group.children = [];
    });
  }).finally(() => {
    // 更新图表渲染
    updateChartRender();
    chartLoading.value = false;
  })
}

/**
 * 执行：更新图表渲染
 * >>> 基于 pageData.cardList 的最新数据
 */
function updateChartRender() {
  const lines = [];
  pageData.cardList.forEach((group, gIdx) => {
    group.children.forEach((line, lIdx) => {
      // 验证数据格式
      if (!Array.isArray(line.data)) {
        console.warn(`曲线 ${line.name} 的数据格式不正确:`, line.data);
        line.data = [];
      }

      const lineOption = {
        name: line.name,
        key: line.key,
        id: line.id,
        isShow: line.isShow,
        data: line.data,
        color: getLineColor(gIdx, lIdx),
        markLine: {
          max: true,
          average: false,
          min: false,
        }
      }
      lines.push(lineOption);
      refreshLineCheckedInfo(line.id, line.isShow);
    })
  })

  chartController.updateTime(pageData.rangeType, pageData.time)
  chartController.executeRender(lines.filter(l => l.isShow));

}


/**
 * 更新lineCheckedInfo
 * >>> 记录每条线当前的选中状态
 */
function refreshLineCheckedInfo(lineId, isChecked) {
  if (isChecked) {
    pageData.lineCheckedInfo.unchecked.delete(lineId);
    pageData.lineCheckedInfo.checked.add(lineId);
  } else {
    pageData.lineCheckedInfo.checked.delete(lineId);
    pageData.lineCheckedInfo.unchecked.add(lineId);
  }
}


/**
 * 判断曲线是否处于选中状态
 * >>> 只要不在unchecked的Set中，就认为是选中状态
 *     >>> 为了让新增的曲线，默认为选中状态
 */
function getLineCheckedState(lineId) {
  const isUnchecked = pageData.lineCheckedInfo.unchecked.has(lineId);
  if (isUnchecked) {
    return false;
  } else {
    return true;
  }
}


/**
 * 获取给定参数需要渲染的需量曲线的数据
 * @param {*} addList 加项
 * @param {*} subList 减项
 * @param {*} meterList 采集需量的电表列表
 */
function getLineData(addList, subList, meterList) {
  const pointsMap = new Map();
  const timeFormatStr = pageData.rangeType === 'month' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss';

  // 处理加项
  addList.forEach(it => {
    console.log(it)
    const meter = it.meterUuid && meterList.find(meter => meter.uuid === it.meterUuid);
    if (Array.isArray(meter?.dataList)) {
      meter.dataList.forEach(p => {
        const time = dayjs(p.record_time).format(timeFormatStr);
        let value = isNaN(p.data) ? 0 : +p.data;

        if (pointsMap.has(time)) {
          const existPointValue = pointsMap.get(time);
          value += existPointValue;
        }

        pointsMap.set(time, value);
      })
    }
  })

  // 处理减项
  subList.forEach(it => {
    const meter = it.meterUuid && meterList.find(meter => meter.uuid === it.meterUuid);
    if (Array.isArray(meter?.dataList)) {
      meter.dataList.forEach(p => {
        const time = dayjs(p.record_time).format(timeFormatStr);
        let value = isNaN(p.data) ? 0 : +p.data;

        if (pointsMap.has(time)) {
          const existPointValue = pointsMap.get(time);
          value = existPointValue - value;
        } else {
          value = 0 - value;
        }

        pointsMap.set(time, value);
      })
    }
  })

  const pointList = Array.from(pointsMap.entries()).sort((a, b) => {
    return dayjs(a[0]).isBefore(dayjs(b[0])) ? -1 : 1;
  })

  return pointList;
}


/**
 * 转换新接口数据格式为图表期望的格式
 * @param {*} rawData 新接口返回的原始数据
 * @returns 转换后的数据格式 [time, value][]
 */
function convertChartDataFormat(rawData) {
  if (!Array.isArray(rawData)) {
    console.warn('convertChartDataFormat: rawData is not an array', rawData);
    return [];
  }

  if (rawData.length === 0) {
    return [];
  }

  // 如果数据已经是 [time, value] 格式，直接返回
  if (Array.isArray(rawData[0]) && rawData[0].length === 2) {
    return rawData;
  }

  // 如果数据是对象数组格式 {time: xxx, value: xxx}，转换为 [time, value] 格式
  if (typeof rawData[0] === 'object' && rawData[0].time !== undefined) {
    return rawData.map(item => [item.time, item.value || item.data || 0]);
  }

  // 如果数据是对象数组格式 {record_time: xxx, data: xxx}，转换为 [time, value] 格式
  if (typeof rawData[0] === 'object' && rawData[0].record_time !== undefined) {
    const timeFormatStr = pageData.rangeType === 'month' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss';
    return rawData.map(item => [
      dayjs(item.record_time).format(timeFormatStr),
      isNaN(item.data) ? 0 : +item.data
    ]);
  }

  // 如果数据是对象数组格式 {timestamp: xxx, value: xxx}，转换为 [time, value] 格式
  if (typeof rawData[0] === 'object' && rawData[0].timestamp !== undefined) {
    const timeFormatStr = pageData.rangeType === 'month' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss';
    return rawData.map(item => [
      dayjs(item.timestamp).format(timeFormatStr),
      isNaN(item.value || item.data) ? 0 : +(item.value || item.data)
    ]);
  }

  console.warn('convertChartDataFormat: unknown data format', rawData);
  return [];
}


/**
 * 获取曲线的初始化颜色
 * @param {*} groupIdx 组索引
 * @param {*} lineIdx 线索引
 * @param {*} step 颜色阶梯变化的步进值
 */
function getLineColor(groupIdx, lineIdx, step = 40) {
  // 实际需量
  if (groupIdx === 0) {
    let [r, g, b, a] = [255, 165, 0, 1]; // r-g-b-a >>> 橙色
    g -= step * lineIdx;
    if (g < 0) { g += 255 }
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  // 储能需量
  if (groupIdx === 1) {
    let [r, g, b, a] = [0, 255, 0, 1]; // r-g-b-a >>> 绿色
    g -= step * lineIdx;
    if (g < 0) { g += 255 }
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  // 负荷需量
  if (groupIdx === 2) {
    let [r, g, b, a] = [41, 210, 255, 1]; // r-g-b-a >>> 蓝色
    r -= step * lineIdx;
    if (r < 0) { r += 255 }
    b += step * lineIdx;
    if (b > 255) { b -= 255 }
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
}


</script>
<style lang="scss" scoped>
.page-container-detail {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  --top-container-height: 70px;
  --bottom-container-height: 70px;

  .top-container {
    height: auto;
    padding: 0 24px 14px 24px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .form-left-items {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .el-form {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }
    }

    .el-radio-group {
      :deep(.el-radio-button) {
        .el-radio-button__inner {
          font-size: 14px;
          color: rgba(46, 46, 46, 0.85);
          height: 32px;
          line-height: 30px;
          padding: 0 16px;
        }

        &.is-active {
          .el-radio-button__inner {
            background-color: #306BF4;
            border-color: #306BF4;
            color: #ffffff;
          }
        }
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container111 {
    box-sizing: border-box;
    // height: calc(100% - var(--top-container-height));
    height: 700px;
    flex: 1;
    width: 100%;
    padding: 20px;
    background: var(--level1-bg-color);
    border: 1px solid rgba(54, 62, 90, 0.7);
    display: flex;
    flex-direction: column;
    gap: 10px;

    .chart-title {
      box-sizing: border-box;
      border-left: 4px solid #485E91;

      height: 22px;
      line-height: 22px;
      padding-left: 12px;
      font-weight: 500;
      font-size: 18px;
      color: #FFFFFF;
      text-align: left;
      font-style: normal;
      text-transform: none;

      display: flex;
      justify-content: space-between;
    }

    .chart-panel {
      flex: 1;
      width: 100%;
    }
  }


  .main-container {
    flex: 1;
    display: flex;
    flex-direction: row;
    gap: 16px;
    min-height: 0;

    .left-container {
      width: 20%;
      height: 100%;
      box-sizing: border-box;
      background: rgba(255, 255, 255, 1);
      border-radius: 20px;
      padding: 16px;
      display: flex;
      flex-direction: column;

      .cards-container {
        height: calc(100% - 50px);
        flex: 1;
        overflow-y: auto;

        // 隐藏滚动条
        :deep(.el-scrollbar__bar.is-vertical),
        .el-virtual-scrollbar.el-vl__vertical {
          display: none;
        }
      }

      .bottom-container {
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 8px;

        // 设置文字类型的btn，hover和focus时，不展示默认背景色
        :deep(.el-button--text:not(.is-disabled)) {
          color: rgba(46, 46, 46, 0.85);
          font-size: 14px;
          padding: 0;

          &:hover,
          &:focus {
            background-color: transparent;
            color: #3a68ff;
          }
        }
      }
    }

    .right-container {
      flex: 1;
      width: 80%;
      box-sizing: border-box;
      background: rgba(255, 255, 255, 1);
      border-radius: 20px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      min-height: 0;
      position: relative;

      .chart-title {
        font-family: 'HarmonyOS Sans SC', sans-serif;
        font-size: 16px;
        color: rgba(46, 46, 46, 1);
        font-weight: 500;
        margin-bottom: 20px;
      }

      .chart-container {
        flex: 1;
        width: 100%;
        min-height: 0;
      }

      .chart-status {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.9);
        z-index: 10;

        .status-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;

          .loading-text,
          .empty-text {
            font-family: "HarmonyOS Sans SC", sans-serif;
            font-size: 14px;
            color: rgba(46, 46, 46, 0.65);
            font-weight: 400;
          }
        }
      }
    }
  }


  // 设置图表tooltip的样式
  ::v-deep .chart-tooltip {
    padding: 12px 16px !important;
    border-radius: 8px !important;
    background: rgba(255, 255, 255, 0.90) !important;
    backdrop-filter: blur(4px);
    border: none !important;
  }

  ::v-deep .chart-tooltip__time {
    margin-bottom: 8px;
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__items-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .chart-tooltip__series {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    line-height: 1.5;
  }

  ::v-deep .chart-tooltip__series-name,
  ::v-deep .chart-tooltip__value {
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__point {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
  }
}

.el-date-editor {
  .el-range__close-icon {
    display: none !important;
  }
}

// 选中日期时的颜色
.el-date-editor.el-input__wrapper:hover {
  box-shadow: none !important;
}
</style>
